/**
 * BlockPalette - Draggable block types that can be added to the email
 * Drag blocks from here and drop them onto columns in the canvas
 */

import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { BlockType } from './types';
import { Type, Image, MousePointer, Minus, ArrowUpDown, GripVertical, LayoutTemplate, Syringe } from 'lucide-react';

interface BlockPaletteItemProps {
  type: BlockType;
  icon: React.ReactNode;
  label: string;
  description: string;
}

const BlockPaletteItem: React.FC<BlockPaletteItemProps> = ({
  type,
  icon,
  label,
  description,
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${type}`,
    data: { type: 'palette-block', blockType: type },
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-grab active:cursor-grabbing ${
        isDragging ? 'opacity-50 ring-2 ring-blue-500' : ''
      }`}
      style={{ 
        backgroundColor: '#ffffff', 
        borderColor: '#e5e7eb',
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = '#dbeafe';
        e.currentTarget.style.borderColor = '#3b82f6';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = '#ffffff';
        e.currentTarget.style.borderColor = '#e5e7eb';
      }}
    >
      <GripVertical className="w-4 h-4 flex-shrink-0" style={{ color: '#6b7280' }} />
      <div 
        className="flex-shrink-0 w-8 h-8 rounded flex items-center justify-center"
        style={{ backgroundColor: '#f3f4f6', color: '#374151' }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium" style={{ color: '#111827' }}>
          {label}
        </div>
        <div className="text-xs truncate" style={{ color: '#6b7280' }}>
          {description}
        </div>
      </div>
    </div>
  );
};

export const BlockPalette: React.FC = () => {
  const blocks: { type: BlockType; icon: React.ReactNode; label: string; description: string }[] = [
    {
      type: 'text',
      icon: <Type className="w-4 h-4" />,
      label: 'Text',
      description: 'Paragraph or heading',
    },
    {
      type: 'image',
      icon: <Image className="w-4 h-4" />,
      label: 'Image',
      description: 'Upload or link image',
    },
    {
      type: 'button',
      icon: <MousePointer className="w-4 h-4" />,
      label: 'Button',
      description: 'Call-to-action button',
    },
    {
      type: 'divider',
      icon: <Minus className="w-4 h-4" />,
      label: 'Divider',
      description: 'Horizontal line',
    },
    {
      type: 'spacer',
      icon: <ArrowUpDown className="w-4 h-4" />,
      label: 'Spacer',
      description: 'Vertical spacing',
    },
    {
      type: 'footer',
      icon: <LayoutTemplate className="w-4 h-4" />,
      label: 'Footer',
      description: 'Email footer with links',
    },
    {
      type: 'vaccine_table',
      icon: <Syringe className="w-4 h-4" />,
      label: 'Vaccine Table',
      description: 'Vaccines list (scheduled/overdue)',
    },
  ];

  return (
    <div>
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
        Content Blocks
      </h3>
      <p className="text-xs text-gray-400 mb-3">Drag to add</p>
      <div className="space-y-2">
        {blocks.map((block) => (
          <BlockPaletteItem
            key={block.type}
            type={block.type}
            icon={block.icon}
            label={block.label}
            description={block.description}
          />
        ))}
      </div>
    </div>
  );
};

